import { defineDomainTranslations } from './defineDomainTranslations';

const EN_VOICE_TRANSLATIONS = {
  'voice.action.apply': 'Apply',
  'voice.action.cancel': 'Cancel',
  'voice.action.regenerate': 'Regenerate',
  'voice.action.save': 'Save',
  'voice.aria.pauseAudio': 'Pause audio',
  'voice.aria.playAudio': 'Play audio',
  'voice.aria.settings': 'Voice settings button',
  'voice.autocomplete.search': 'Search for audio',
  'voice.autocomplete.select': 'Select audio',
  'voice.create.generate': 'Generate Voice Clone',
  'voice.create.nameLabel': 'Voice name',
  'voice.create.namePlaceholder': 'Enter a name',
  'voice.create.subtitle':
    'Create a Custom Voice Clone to use across projects by uploading files containing recordings of your voice.',
  'voice.create.title': 'New Custom Voice Clone',
  'voice.error.generatePreview': 'Failed to generate the preview',
  'voice.error.insufficientSpeech':
    'Not enough speech to clone this voice. Edit speaker assignment or use recommended presets.',
  'voice.error.sampleUnavailable': 'The voice preview is unavailable',
  'voice.error.saveToLibrary': 'Failed to save the Voice Clone to your library',
  'voice.group.aiPresets': 'AI voice presets',
  'voice.group.instantClone': 'Instant Voice Clone',
  'voice.group.library': 'Voice Clone library',
  'voice.hint.aiNotCustomizable': 'The AI voice preset cannot be customized.',
  'voice.hint.createOwnAfter': 'to customize emotion and accent.',
  'voice.hint.createOwnBefore': 'You can',
  'voice.hint.createOwnLink': 'create your own voice',
  'voice.hint.planAccess': 'Voice customization and saving to library are available from the Creator Pro plan.',
  'voice.hint.upgradeAll': 'Upgrade to use all these features!',
  'voice.instant.generatePreview': 'Generate preview',
  'voice.instant.playPreview': 'Play preview',
  'voice.instant.saveToLibrary': 'Save to library',
  'voice.instant.savingTooltip': "We're saving the voice to the library. You'll be able to use it once it's saved.",
  'voice.label.speaker': 'Speaker',
  'voice.label.voice': 'Voice',
  'voice.library.add': 'Add Voice Clone',
  'voice.library.create': 'Create Voice Clone',
  'voice.library.description':
    'Create high-quality voice clones with full control over settings and use them across projects for consistent results.',
  'voice.library.dropHere': 'Drop it here',
  'voice.library.newTitle': 'New Voice Clone',
  'voice.library.upgradeToCreate': 'Upgrade to create Voice Clone',
  'voice.library.voicesAvailable': 'Voices available: {count}',
  'voice.option.instantClone': 'Voice Clone',
  'voice.settings.applyInstruction':
    'To use the updated Voice Clone, apply it here and then apply the changes to the project.',
  'voice.settings.editFilesTitle': 'Edit',
  'voice.settings.editSamples': 'Edit samples',
  'voice.settings.editVoice': 'Edit Voice',
  'voice.settings.title': 'Voice settings',
  'voice.settings.updateCaption': 'Apply changes to previous projects to use the new voice settings.',
  'voice.slider.accent.maximum':
    "Maximum = the cloned voice closely matches the original voice and might include the original speaker's accent. Recommended for high-quality audio without noise, as AI can replicate it effectively.",
  'voice.slider.accent.minimum':
    'Minimum = suitable for samples with background artifacts, but voice similarity may decrease.',
  'voice.slider.accent.title': 'Similarity & accent',
  'voice.slider.expressiveness.maximum':
    'Maximum = the voice will be more emotional and variable, but less stable and consistent throughout your video.',
  'voice.slider.expressiveness.minimum':
    'Minimum = the voice will be more stable and consistent, but it might lack emotion.',
  'voice.slider.expressiveness.title': 'Expressiveness',
  'voice.speakers.subtitle':
    "Here you can see all the speakers' voices in the project and choose another voice for a speaker.",
  'voice.speakers.title': 'Speaker voices',
  'voice.status.failed': 'Failed',
  'voice.status.inProgress': 'In progress',
} as const;

const CORE_VOICE_TRANSLATIONS = defineDomainTranslations({
  en: EN_VOICE_TRANSLATIONS,
  es: {
    'voice.action.apply': 'Aplicar',
    'voice.action.cancel': 'Cancelar',
    'voice.action.regenerate': 'Regenerar',
    'voice.action.save': 'Guardar',
    'voice.aria.pauseAudio': 'Botón para pausar el audio',
    'voice.aria.playAudio': 'Botón para reproducir el audio',
    'voice.aria.settings': 'Botón de ajustes de voz',
    'voice.autocomplete.search': 'Buscar un audio',
    'voice.autocomplete.select': 'Seleccionar un audio',
    'voice.create.generate': 'Generar clon de voz',
    'voice.create.nameLabel': 'Nombre de la voz',
    'voice.create.namePlaceholder': 'Introduce un nombre',
    'voice.create.subtitle':
      'Crea un clon de voz personalizado para usarlo en distintos proyectos subiendo archivos que contengan grabaciones de tu voz.',
    'voice.create.title': 'Nuevo clon de voz personalizado',
    'voice.error.generatePreview': 'No se ha podido generar la vista previa',
    'voice.error.insufficientSpeech':
      'No hay suficiente audio del hablante para clonar esta voz. Cambia la asignación del hablante o utiliza los preajustes recomendados.',
    'voice.error.sampleUnavailable': 'La vista previa de la voz no está disponible',
    'voice.error.saveToLibrary': 'No se ha podido guardar el clon de voz en tu biblioteca',
    'voice.group.aiPresets': 'Preajustes de voz con IA',
    'voice.group.instantClone': 'Clonación de voz instantánea',
    'voice.group.library': 'Biblioteca de voces clonadas',
    'voice.hint.aiNotCustomizable': 'El preajuste de voz con IA no se puede personalizar.',
    'voice.hint.createOwnAfter': 'para personalizar la emoción y el acento.',
    'voice.hint.createOwnBefore': 'Puedes',
    'voice.hint.createOwnLink': 'crear tu propia voz',
    'voice.hint.planAccess':
      'La personalización de voz y la opción de guardarla en la biblioteca están disponibles en el plan Creator Pro.',
    'voice.hint.upgradeAll': '¡Mejora tu plan para usar todas estas funciones!',
    'voice.instant.generatePreview': 'Generar vista previa',
    'voice.instant.playPreview': 'Reproducir vista previa',
    'voice.instant.saveToLibrary': 'Guardar en la biblioteca',
    'voice.instant.savingTooltip':
      'Estamos guardando la voz en la biblioteca. Podrás usarla cuando termine el proceso.',
    'voice.label.speaker': 'Hablante',
    'voice.label.voice': 'Voz',
    'voice.library.add': 'Añadir clon de voz',
    'voice.library.create': 'Crear clon de voz',
    'voice.library.description':
      'Crea clones de voz de alta calidad con control total sobre los ajustes y úsalos en distintos proyectos para obtener resultados consistentes.',
    'voice.library.dropHere': 'Suéltalo aquí',
    'voice.library.newTitle': 'Nuevo clon de voz',
    'voice.library.upgradeToCreate': 'Mejora tu plan para crear un clon de voz',
    'voice.library.voicesAvailable': 'Voces disponibles: {count}',
    'voice.option.instantClone': 'Clonación de voz',
    'voice.settings.applyInstruction':
      'Para usar la clonación de voz actualizada, aplícala aquí y después aplica los cambios al proyecto.',
    'voice.settings.editFilesTitle': 'Editar',
    'voice.settings.editSamples': 'Editar muestras',
    'voice.settings.editVoice': 'Editar voz',
    'voice.settings.title': 'Ajustes de voz',
    'voice.settings.updateCaption':
      'Aplica los cambios a los proyectos anteriores para usar los nuevos ajustes de voz.',
    'voice.slider.accent.maximum':
      'Máximo = la voz clonada se parece mucho a la original y puede conservar el acento del hablante. Se recomienda para audio de alta calidad y sin ruido, que la IA puede reproducir con precisión.',
    'voice.slider.accent.minimum':
      'Mínimo = adecuado para muestras con ruido o artefactos de fondo, aunque la similitud de la voz puede disminuir.',
    'voice.slider.accent.title': 'Similitud y acento',
    'voice.slider.expressiveness.maximum':
      'Máximo = la voz será más emotiva y variable, pero menos estable y uniforme a lo largo del video.',
    'voice.slider.expressiveness.minimum':
      'Mínimo = la voz será más estable y uniforme, pero puede resultar menos emotiva.',
    'voice.slider.expressiveness.title': 'Expresividad',
    'voice.speakers.subtitle':
      'Aquí puedes ver las voces de todos los hablantes del proyecto y elegir otra voz para cada uno.',
    'voice.speakers.title': 'Voces de los hablantes',
    'voice.status.failed': 'Error',
    'voice.status.inProgress': 'En curso',
  },
  fr: {
    'voice.action.apply': 'Appliquer',
    'voice.action.cancel': 'Annuler',
    'voice.action.regenerate': 'Régénérer',
    'voice.action.save': 'Enregistrer',
    'voice.aria.pauseAudio': 'Bouton de mise en pause de l’audio',
    'voice.aria.playAudio': 'Bouton de lecture de l’audio',
    'voice.aria.settings': 'Bouton des paramètres de voix',
    'voice.autocomplete.search': 'Rechercher un audio',
    'voice.autocomplete.select': 'Sélectionner un audio',
    'voice.create.generate': 'Générer la voix clonée',
    'voice.create.nameLabel': 'Nom de la voix',
    'voice.create.namePlaceholder': 'Saisissez un nom',
    'voice.create.subtitle':
      'Créez une voix clonée personnalisée utilisable dans plusieurs projets en important des fichiers contenant votre voix originale.',
    'voice.create.title': 'Nouvelle voix clonée personnalisée',
    'voice.error.generatePreview': 'La génération de l’aperçu a échoué',
    'voice.error.insufficientSpeech':
      'La quantité de parole est insuffisante pour cloner cette voix. Modifiez l’attribution du locuteur ou utilisez les voix recommandées.',
    'voice.error.sampleUnavailable': 'L’aperçu de la voix n’est pas disponible',
    'voice.error.saveToLibrary': 'Échec de l’enregistrement de la voix clonée dans votre bibliothèque',
    'voice.group.aiPresets': 'Voix IA prédéfinies',
    'voice.group.instantClone': 'Clonage vocal instantané',
    'voice.group.library': 'Bibliothèque de voix clonées',
    'voice.hint.aiNotCustomizable': 'Le préréglage de voix IA ne peut pas être personnalisé.',
    'voice.hint.createOwnAfter': 'pour personnaliser l’émotion et l’accent.',
    'voice.hint.createOwnBefore': 'Vous pouvez',
    'voice.hint.createOwnLink': 'créer votre propre voix',
    'voice.hint.planAccess':
      'La personnalisation de la voix et son enregistrement dans la bibliothèque sont disponibles avec le forfait Creator Pro.',
    'voice.hint.upgradeAll': 'Passez au forfait supérieur pour utiliser toutes ces fonctionnalités !',
    'voice.instant.generatePreview': 'Générer un aperçu',
    'voice.instant.playPreview': 'Lire l’aperçu',
    'voice.instant.saveToLibrary': 'Enregistrer dans la bibliothèque',
    'voice.instant.savingTooltip':
      'Nous enregistrons la voix dans la bibliothèque. Vous pourrez l’utiliser une fois l’opération terminée.',
    'voice.label.speaker': 'Locuteur',
    'voice.label.voice': 'Voix',
    'voice.library.add': 'Ajouter une voix clonée',
    'voice.library.create': 'Créer une voix clonée',
    'voice.library.description':
      'Créez des voix clonées de haute qualité avec un contrôle total des paramètres et utilisez-les dans plusieurs projets pour obtenir des résultats cohérents.',
    'voice.library.dropHere': 'Déposez-le ici',
    'voice.library.newTitle': 'Nouvelle voix clonée',
    'voice.library.upgradeToCreate': 'Passez au forfait supérieur pour créer une voix clonée',
    'voice.library.voicesAvailable': 'Voix disponibles : {count}',
    'voice.option.instantClone': 'Clonage vocal',
    'voice.settings.applyInstruction':
      'Pour utiliser la voix clonée mise à jour, appliquez-la ici, puis appliquez les modifications au projet.',
    'voice.settings.editFilesTitle': 'Modifier',
    'voice.settings.editSamples': 'Modifier les échantillons',
    'voice.settings.editVoice': 'Modifier la voix',
    'voice.settings.title': 'Paramètres de la voix',
    'voice.settings.updateCaption':
      'Appliquez les modifications aux projets précédents pour utiliser les nouveaux paramètres de voix.',
    'voice.slider.accent.maximum':
      'Maximum = la voix clonée est très proche de la voix originale et peut conserver l’accent du locuteur. Recommandé pour un son de haute qualité sans bruit, car l’IA peut alors le reproduire fidèlement.',
    'voice.slider.accent.minimum':
      'Minimum = adapté aux échantillons contenant du bruit ou des artefacts de fond, mais la ressemblance vocale peut diminuer.',
    'voice.slider.accent.title': 'Ressemblance et accent',
    'voice.slider.expressiveness.maximum':
      'Maximum = la voix sera plus expressive et variable, mais moins stable et régulière tout au long de la vidéo.',
    'voice.slider.expressiveness.minimum':
      'Minimum = la voix sera plus stable et régulière, mais pourra manquer d’émotion.',
    'voice.slider.expressiveness.title': 'Expressivité',
    'voice.speakers.subtitle':
      'Vous pouvez voir ici les voix de tous les locuteurs du projet et choisir une autre voix pour un locuteur.',
    'voice.speakers.title': 'Voix des locuteurs',
    'voice.status.failed': 'Échec',
    'voice.status.inProgress': 'En cours',
  },
  it: {
    'voice.action.apply': 'Applica',
    'voice.action.cancel': 'Annulla',
    'voice.action.regenerate': 'Rigenera',
    'voice.action.save': 'Salva',
    'voice.aria.pauseAudio': 'Pulsante per mettere in pausa l’audio',
    'voice.aria.playAudio': 'Pulsante per riprodurre l’audio',
    'voice.aria.settings': 'Apri impostazioni voce',
    'voice.autocomplete.search': 'Cerca un audio',
    'voice.autocomplete.select': 'Seleziona un audio',
    'voice.create.generate': 'Genera voce clonata',
    'voice.create.nameLabel': 'Nome della voce',
    'voice.create.namePlaceholder': 'Inserisci un nome',
    'voice.create.subtitle':
      'Crea una voce clonata personalizzata da usare in più progetti caricando file con la tua voce originale.',
    'voice.create.title': 'Nuova voce clonata personalizzata',
    'voice.error.generatePreview': 'Impossibile generare l’anteprima',
    'voice.error.insufficientSpeech':
      'Il parlato non è sufficiente per clonare questa voce. Modifica l’assegnazione del parlante o usa i preset consigliati.',
    'voice.error.sampleUnavailable': 'L’anteprima della voce non è disponibile',
    'voice.error.saveToLibrary': 'Impossibile salvare la voce clonata nella tua libreria',
    'voice.group.aiPresets': 'Voci preimpostate con IA',
    'voice.group.instantClone': 'Clonazione vocale istantanea',
    'voice.group.library': 'Libreria delle voci clonate',
    'voice.hint.aiNotCustomizable': 'La voce preimpostata con IA non può essere personalizzata.',
    'voice.hint.createOwnAfter': 'per personalizzare l’emozione e l’accento.',
    'voice.hint.createOwnBefore': 'Puoi',
    'voice.hint.createOwnLink': 'creare la tua voce',
    'voice.hint.planAccess':
      'La personalizzazione della voce e il salvataggio nella libreria sono disponibili a partire dal piano Creator Pro.',
    'voice.hint.upgradeAll': 'Passa a un piano superiore per usare tutte queste funzionalità!',
    'voice.instant.generatePreview': 'Genera anteprima',
    'voice.instant.playPreview': 'Riproduci anteprima',
    'voice.instant.saveToLibrary': 'Salva nella libreria',
    'voice.instant.savingTooltip': 'Stiamo salvando la voce nella libreria. Potrai usarla al termine dell’operazione.',
    'voice.label.speaker': 'Speaker',
    'voice.label.voice': 'Voce',
    'voice.library.add': 'Aggiungi una voce clonata',
    'voice.library.create': 'Crea una voce clonata',
    'voice.library.description':
      'Crea voci clonate di alta qualità con il pieno controllo delle impostazioni e usale in più progetti per ottenere risultati coerenti.',
    'voice.library.dropHere': 'Rilascialo qui',
    'voice.library.newTitle': 'Nuova voce clonata',
    'voice.library.upgradeToCreate': 'Passa a un piano superiore per creare una voce clonata',
    'voice.library.voicesAvailable': 'Voci disponibili: {count}',
    'voice.option.instantClone': 'Clonazione vocale',
    'voice.settings.applyInstruction':
      'Per usare la voce clonata aggiornata, applicala qui e poi applica le modifiche al progetto.',
    'voice.settings.editFilesTitle': 'Modifica',
    'voice.settings.editSamples': 'Modifica campioni',
    'voice.settings.editVoice': 'Modifica voce',
    'voice.settings.title': 'Impostazioni della voce',
    'voice.settings.updateCaption':
      'Applica le modifiche ai progetti precedenti per usare le nuove impostazioni della voce.',
    'voice.slider.accent.maximum':
      'Massimo = la voce clonata somiglia molto all’originale e può mantenere l’accento del parlante. Consigliato per audio di alta qualità e senza rumore, che l’IA può riprodurre con precisione.',
    'voice.slider.accent.minimum':
      'Minimo = adatto a campioni con rumori o artefatti di fondo, ma la somiglianza della voce potrebbe diminuire.',
    'voice.slider.accent.title': 'Somiglianza e accento',
    'voice.slider.expressiveness.maximum':
      'Massimo = la voce sarà più emotiva e variabile, ma meno stabile e uniforme durante il video.',
    'voice.slider.expressiveness.minimum':
      'Minimo = la voce sarà più stabile e uniforme, ma potrebbe risultare meno emotiva.',
    'voice.slider.expressiveness.title': 'Espressività',
    'voice.speakers.subtitle':
      'Qui puoi vedere le voci di tutti i parlanti del progetto e scegliere una voce diversa per ciascuno.',
    'voice.speakers.title': 'Voci dei parlanti',
    'voice.status.failed': 'Operazione non riuscita',
    'voice.status.inProgress': 'In corso',
  },
  pt: {
    'voice.action.apply': 'Aplicar',
    'voice.action.cancel': 'Cancelar',
    'voice.action.regenerate': 'Gerar novamente',
    'voice.action.save': 'Salvar',
    'voice.aria.pauseAudio': 'Botão para pausar o áudio',
    'voice.aria.playAudio': 'Botão para reproduzir o áudio',
    'voice.aria.settings': 'Botão de configurações de voz',
    'voice.autocomplete.search': 'Buscar áudio',
    'voice.autocomplete.select': 'Selecionar áudio',
    'voice.create.generate': 'Gerar clonagem de voz',
    'voice.create.nameLabel': 'Nome da voz',
    'voice.create.namePlaceholder': 'Digite um nome',
    'voice.create.subtitle':
      'Crie uma clonagem de voz personalizada para usar em vários projetos enviando arquivos com sua voz original.',
    'voice.create.title': 'Nova clonagem de voz personalizada',
    'voice.error.generatePreview': 'Não foi possível gerar a prévia',
    'voice.error.insufficientSpeech':
      'Não há fala suficiente para clonar esta voz. Altere a atribuição do locutor ou use as predefinições recomendadas.',
    'voice.error.sampleUnavailable': 'A prévia da voz não está disponível',
    'voice.error.saveToLibrary': 'Não foi possível salvar a clonagem de voz na biblioteca',
    'voice.group.aiPresets': 'Predefinições de voz com IA',
    'voice.group.instantClone': 'Clonagem de voz instantânea',
    'voice.group.library': 'Biblioteca de vozes clonadas',
    'voice.hint.aiNotCustomizable': 'A predefinição de voz com IA não pode ser personalizada.',
    'voice.hint.createOwnAfter': 'para personalizar a emoção e o sotaque.',
    'voice.hint.createOwnBefore': 'Você pode',
    'voice.hint.createOwnLink': 'criar sua própria voz',
    'voice.hint.planAccess':
      'A personalização de voz e a opção de salvá-la na biblioteca estão disponíveis no plano Creator Pro.',
    'voice.hint.upgradeAll': 'Faça upgrade para usar todos esses recursos!',
    'voice.instant.generatePreview': 'Gerar prévia',
    'voice.instant.playPreview': 'Reproduzir prévia',
    'voice.instant.saveToLibrary': 'Salvar na biblioteca',
    'voice.instant.savingTooltip':
      'Estamos salvando a voz na biblioteca. Você poderá usá-la quando o processo terminar.',
    'voice.label.speaker': 'Locutor',
    'voice.label.voice': 'Voz',
    'voice.library.add': 'Adicionar clonagem de voz',
    'voice.library.create': 'Criar clonagem de voz',
    'voice.library.description':
      'Crie clonagens de voz de alta qualidade com controle total das configurações e use-as em vários projetos para obter resultados consistentes.',
    'voice.library.dropHere': 'Solte aqui',
    'voice.library.newTitle': 'Nova clonagem de voz',
    'voice.library.upgradeToCreate': 'Faça upgrade do plano para criar uma clonagem de voz',
    'voice.library.voicesAvailable': 'Vozes disponíveis: {count}',
    'voice.option.instantClone': 'Clonagem de voz',
    'voice.settings.applyInstruction':
      'Para usar a clonagem de voz atualizada, aplique-a aqui e depois aplique as alterações ao projeto.',
    'voice.settings.editFilesTitle': 'Editar',
    'voice.settings.editSamples': 'Editar amostras',
    'voice.settings.editVoice': 'Editar voz',
    'voice.settings.title': 'Configurações de voz',
    'voice.settings.updateCaption':
      'Aplique as alterações aos projetos anteriores para usar as novas configurações de voz.',
    'voice.slider.accent.maximum':
      'Máximo = a voz clonada fica muito semelhante à original e pode incluir o sotaque do locutor. Recomendado para áudio de alta qualidade e sem ruído, que a IA consegue reproduzir com precisão.',
    'voice.slider.accent.minimum':
      'Mínimo = adequado para amostras com barulhos de fundo, mas a semelhança da voz pode diminuir.',
    'voice.slider.accent.title': 'Semelhança e sotaque',
    'voice.slider.expressiveness.maximum':
      'Máximo = a voz será mais emotiva e variável, mas menos estável e uniforme ao longo do vídeo.',
    'voice.slider.expressiveness.minimum': 'Mínimo = a voz será mais estável e uniforme, mas poderá ter menos emoção.',
    'voice.slider.expressiveness.title': 'Expressividade',
    'voice.speakers.subtitle':
      'Aqui você pode ver as vozes de todos os locutores do projeto e escolher outra voz para um locutor.',
    'voice.speakers.title': 'Vozes dos locutores',
    'voice.status.failed': 'Falha',
    'voice.status.inProgress': 'Em andamento',
  },
  ru: {
    'voice.action.apply': 'Применить',
    'voice.action.cancel': 'Отмена',
    'voice.action.regenerate': 'Создать заново',
    'voice.action.save': 'Сохранить',
    'voice.aria.pauseAudio': 'Приостановить аудио',
    'voice.aria.playAudio': 'Воспроизвести аудио',
    'voice.aria.settings': 'Открыть настройки голоса',
    'voice.autocomplete.search': 'Найти голос',
    'voice.autocomplete.select': 'Выбрать голос',
    'voice.create.generate': 'Создать клон голоса',
    'voice.create.nameLabel': 'Название голоса',
    'voice.create.namePlaceholder': 'Введите название',
    'voice.create.subtitle':
      'Создайте собственный клон голоса для разных проектов, загрузив файлы с оригинальной записью вашего голоса.',
    'voice.create.title': 'Новый собственный клон голоса',
    'voice.error.generatePreview': 'Не удалось создать предпрослушивание',
    'voice.error.insufficientSpeech':
      'Недостаточно речи для клонирования голоса. Измените назначение спикера или выберите один из рекомендуемых голосов.',
    'voice.error.sampleUnavailable': 'Предпрослушивание голоса недоступно',
    'voice.error.saveToLibrary': 'Не удалось сохранить клон голоса в библиотеку',
    'voice.group.aiPresets': 'Готовые ИИ-голоса',
    'voice.group.instantClone': 'Мгновенное клонирование голоса',
    'voice.group.library': 'Библиотека клонов голоса',
    'voice.hint.aiNotCustomizable': 'Готовый ИИ-голос нельзя настроить.',
    'voice.hint.createOwnAfter': 'и настроить его эмоциональность и акцент.',
    'voice.hint.createOwnBefore': 'Вы можете',
    'voice.hint.createOwnLink': 'создать собственный голос',
    'voice.hint.planAccess': 'Настройка голоса и сохранение в библиотеку доступны начиная с тарифа Creator Pro.',
    'voice.hint.upgradeAll': 'Перейдите на более высокий тариф, чтобы использовать все эти функции!',
    'voice.instant.generatePreview': 'Создать аудиопример',
    'voice.instant.playPreview': 'Прослушать',
    'voice.instant.saveToLibrary': 'Сохранить в библиотеку',
    'voice.instant.savingTooltip':
      'Голос сохраняется в библиотеку. Вы сможете использовать его, когда сохранение завершится.',
    'voice.label.speaker': 'Спикер',
    'voice.label.voice': 'Голос',
    'voice.library.add': 'Добавить клон голоса',
    'voice.library.create': 'Создать клон голоса',
    'voice.library.description':
      'Создавайте качественные клоны голосов с полной настройкой параметров и используйте их в разных проектах для стабильного результата.',
    'voice.library.dropHere': 'Перетащите сюда',
    'voice.library.newTitle': 'Новый клон голоса',
    'voice.library.upgradeToCreate': 'Повысьте тариф, чтобы создать клон голоса',
    'voice.library.voicesAvailable': 'Доступно голосов: {count}',
    'voice.option.instantClone': 'Клонирование голоса',
    'voice.settings.applyInstruction':
      'Чтобы использовать обновлённый клон голоса, примените его здесь, а затем примените изменения в проекте.',
    'voice.settings.editFilesTitle': 'Редактирование',
    'voice.settings.editSamples': 'Изменить образцы',
    'voice.settings.editVoice': 'Настроить голос',
    'voice.settings.title': 'Настройки голоса',
    'voice.settings.updateCaption':
      'Примените изменения к предыдущим проектам, чтобы использовать новые настройки голоса.',
    'voice.slider.accent.maximum':
      'Максимум = клон максимально похож на оригинальный голос и может сохранить акцент спикера. Рекомендуется для качественного аудио без шума, поскольку оно позволяет ИИ точнее воспроизвести особенности голоса.',
    'voice.slider.accent.minimum':
      'Минимум = подходит для записей с фоновым шумом или артефактами, но сходство голоса может снизиться.',
    'voice.slider.accent.title': 'Сходство и акцент',
    'voice.slider.expressiveness.maximum':
      'Максимум = голос будет эмоциональнее и разнообразнее, но менее стабилен на протяжении видео.',
    'voice.slider.expressiveness.minimum':
      'Минимум = голос будет стабильнее и ровнее, но может звучать менее эмоционально.',
    'voice.slider.expressiveness.title': 'Выразительность',
    'voice.speakers.subtitle':
      'Здесь можно просмотреть голоса всех спикеров проекта и выбрать для каждого другой голос.',
    'voice.speakers.title': 'Голоса спикеров',
    'voice.status.failed': 'Ошибка',
    'voice.status.inProgress': 'В процессе',
  },
});

const VOICE_FEATURE_TRANSLATIONS = defineDomainTranslations({
  en: {
    'voice.action.delete': 'Delete',
    'voice.aria.removeSample': 'Remove voice sample',
    'voice.delete.subtitle':
      'Once deleted, this Custom Voice Clone will no longer be accessible. If you use it in your projects, it will be replaced with Instant Voice Clone.',
    'voice.delete.title': 'Would you like to permanently delete this Custom Voice Clone?',
    'voice.dropzone.choose': 'Click to choose a file',
    'voice.dropzone.drag': 'or drag and drop it here',
    'voice.dropzone.upload': 'Click to upload a file',
    'voice.error.fileInvalidType': 'Invalid file type',
    'voice.error.fileTooBig': 'File is too big',
    'voice.error.fileTooSmall': 'File is too small',
    'voice.error.genericRetryLater': 'Something went wrong. Please try again later.',
    'voice.error.invalidLink': 'Please provide a valid link',
    'voice.error.maxDuration': 'Maximum duration is 10 minutes',
    'voice.error.maxSamples': 'You can upload up to 10 samples',
    'voice.error.prefix': 'Error: {message}',
    'voice.error.tooManyFiles': 'Too many files',
    'voice.label.name': 'Name',
    'voice.language.englishUS': 'English (US)',
    'voice.rename.namePlaceholder': 'Enter a new name',
    'voice.rename.title': 'Rename Custom Voice Clone',
    'voice.samples.count': 'Samples {quantity}/{maxQuantity}',
    'voice.tips.first': 'Audio or video from 1 to 10 minutes long, up to 10 MB each',
    'voice.tips.fourth':
      'For a consistent voiceover, avoid uploading samples recorded in different environments or with different speaking styles',
    'voice.tips.proLabel': 'Pro tip:',
    'voice.tips.proText':
      'After creating the voice, you can adjust its similarity and expressiveness. Create separate voices for different speaking styles, tones, and emotions.',
    'voice.tips.second': 'One speaker with a consistent speaking style and few or no long pauses',
    'voice.tips.third': 'High-quality sound without background noise',
    'voice.upload.linkPlaceholder': 'Paste a YouTube or Vimeo link',
    'voice.upload.or': 'or',
  },
  es: {
    'voice.action.delete': 'Eliminar',
    'voice.aria.removeSample': 'Eliminar muestra de voz',
    'voice.delete.subtitle':
      'Después de eliminarla, esta clonación de voz personalizada dejará de estar disponible. Si la usas en algún proyecto, se sustituirá por la clonación de voz instantánea.',
    'voice.delete.title': '¿Quieres eliminar permanentemente esta clonación de voz personalizada?',
    'voice.dropzone.choose': 'Haz clic para elegir un archivo',
    'voice.dropzone.drag': 'o arrástralo y suéltalo aquí',
    'voice.dropzone.upload': 'Haz clic para subir un archivo',
    'voice.error.fileInvalidType': 'Tipo de archivo no válido',
    'voice.error.fileTooBig': 'El archivo es demasiado grande',
    'voice.error.fileTooSmall': 'El archivo es demasiado pequeño',
    'voice.error.genericRetryLater': 'Algo ha salido mal. Inténtalo de nuevo más tarde.',
    'voice.error.invalidLink': 'Introduce un enlace válido',
    'voice.error.maxDuration': 'La duración máxima es de 10 minutos',
    'voice.error.maxSamples': 'Puedes subir hasta 10 muestras',
    'voice.error.prefix': 'Error: {message}',
    'voice.error.tooManyFiles': 'Demasiados archivos',
    'voice.label.name': 'Nombre',
    'voice.language.englishUS': 'Inglés (EE. UU.)',
    'voice.rename.namePlaceholder': 'Introduce un nuevo nombre',
    'voice.rename.title': 'Cambiar el nombre del clon de voz personalizado',
    'voice.samples.count': 'Muestras {quantity}/{maxQuantity}',
    'voice.tips.first': 'Audio o video de entre 1 y 10 minutos, con un máximo de 10 MB por archivo',
    'voice.tips.fourth':
      'Para obtener una voz uniforme, evita subir muestras grabadas en entornos distintos o con estilos de habla diferentes',
    'voice.tips.proLabel': 'Consejo:',
    'voice.tips.proText':
      'Después de crear la voz, podrás ajustar su similitud y expresividad. Crea voces distintas para diferentes estilos de habla, tonos y emociones.',
    'voice.tips.second': 'Un solo hablante con un estilo de habla uniforme y pocas pausas largas o ninguna',
    'voice.tips.third': 'Sonido de alta calidad sin ruido de fondo',
    'voice.upload.linkPlaceholder': 'Pega un enlace de YouTube o Vimeo',
    'voice.upload.or': 'o',
  },
  fr: {
    'voice.action.delete': 'Supprimer',
    'voice.aria.removeSample': 'Supprimer l’échantillon vocal',
    'voice.delete.subtitle':
      'Après sa suppression, cette voix clonée personnalisée ne sera plus accessible. Si elle est utilisée dans vos projets, elle sera remplacée par le clonage vocal instantané.',
    'voice.delete.title': 'Voulez-vous supprimer définitivement cette voix clonée personnalisée ?',
    'voice.dropzone.choose': 'Cliquez pour choisir un fichier',
    'voice.dropzone.drag': 'ou glissez-déposez-le ici',
    'voice.dropzone.upload': 'Cliquez pour importer un fichier',
    'voice.error.fileInvalidType': 'Type de fichier non valide',
    'voice.error.fileTooBig': 'Le fichier est trop volumineux',
    'voice.error.fileTooSmall': 'Le fichier est trop petit',
    'voice.error.genericRetryLater': 'Une erreur est survenue. Réessayez plus tard.',
    'voice.error.invalidLink': 'Saisissez un lien valide',
    'voice.error.maxDuration': 'La durée maximale est de 10 minutes',
    'voice.error.maxSamples': 'Vous pouvez importer jusqu’à 10 échantillons',
    'voice.error.prefix': 'Erreur : {message}',
    'voice.error.tooManyFiles': 'Trop de fichiers',
    'voice.label.name': 'Nom',
    'voice.language.englishUS': 'Anglais (États-Unis)',
    'voice.rename.namePlaceholder': 'Saisissez un nouveau nom',
    'voice.rename.title': 'Renommer la voix clonée personnalisée',
    'voice.samples.count': 'Échantillons {quantity}/{maxQuantity}',
    'voice.tips.first': 'Audio ou vidéo de 1 à 10 minutes, 10 Mo maximum par fichier',
    'voice.tips.fourth':
      'Pour obtenir une voix homogène, évitez les échantillons enregistrés dans des environnements ou avec des styles de parole différents',
    'voice.tips.proLabel': 'Conseil :',
    'voice.tips.proText':
      'Après avoir créé la voix, vous pourrez ajuster sa similarité et son expressivité. Créez des voix distinctes pour différents styles de parole, tons et émotions.',
    'voice.tips.second': 'Un seul locuteur au style régulier, avec peu de longues pauses',
    'voice.tips.third': 'Son de haute qualité sans bruit de fond',
    'voice.upload.linkPlaceholder': 'Collez un lien YouTube ou Vimeo',
    'voice.upload.or': 'ou',
  },
  it: {
    'voice.action.delete': 'Elimina',
    'voice.aria.removeSample': 'Rimuovi campione vocale',
    'voice.delete.subtitle':
      'Dopo l’eliminazione, questa voce clonata personalizzata non sarà più accessibile. Se è usata nei tuoi progetti, verrà sostituita dalla clonazione vocale istantanea.',
    'voice.delete.title': 'Vuoi eliminare definitivamente questa voce clonata personalizzata?',
    'voice.dropzone.choose': 'Fai clic per scegliere un file',
    'voice.dropzone.drag': 'oppure trascinalo qui',
    'voice.dropzone.upload': 'Fai clic per caricare un file',
    'voice.error.fileInvalidType': 'Tipo di file non valido',
    'voice.error.fileTooBig': 'Il file è troppo grande',
    'voice.error.fileTooSmall': 'Il file è troppo piccolo',
    'voice.error.genericRetryLater': 'Qualcosa è andato storto. Riprova più tardi.',
    'voice.error.invalidLink': 'Inserisci un link valido',
    'voice.error.maxDuration': 'La durata massima è di 10 minuti',
    'voice.error.maxSamples': 'Puoi caricare fino a 10 campioni',
    'voice.error.prefix': 'Errore: {message}',
    'voice.error.tooManyFiles': 'Troppi file',
    'voice.label.name': 'Nome',
    'voice.language.englishUS': 'Inglese (Stati Uniti)',
    'voice.rename.namePlaceholder': 'Inserisci un nuovo nome',
    'voice.rename.title': 'Rinomina la voce clonata personalizzata',
    'voice.samples.count': 'Campioni {quantity}/{maxQuantity}',
    'voice.tips.first': 'Audio o video da 1 a 10 minuti, massimo 10 MB per file',
    'voice.tips.fourth':
      'Per ottenere una voce uniforme, evita campioni registrati in ambienti diversi o con stili di parlato differenti',
    'voice.tips.proLabel': 'Suggerimento:',
    'voice.tips.proText':
      'Dopo aver creato la voce, potrai regolarne la somiglianza e l’espressività. Crea voci separate per stili di parlato, toni ed emozioni diversi.',
    'voice.tips.second': 'Un solo parlante con uno stile uniforme e poche pause lunghe',
    'voice.tips.third': 'Audio di alta qualità senza rumore di fondo',
    'voice.upload.linkPlaceholder': 'Incolla un link di YouTube o Vimeo',
    'voice.upload.or': 'oppure',
  },
  pt: {
    'voice.action.delete': 'Excluir',
    'voice.aria.removeSample': 'Remover amostra de voz',
    'voice.delete.subtitle':
      'Depois de excluída, esta clonagem de voz personalizada não ficará mais disponível. Se ela for usada nos seus projetos, será substituída pela clonagem de voz instantânea.',
    'voice.delete.title': 'Deseja excluir permanentemente esta clonagem de voz personalizada?',
    'voice.dropzone.choose': 'Clique para escolher um arquivo',
    'voice.dropzone.drag': 'ou arraste e solte aqui',
    'voice.dropzone.upload': 'Clique para enviar um arquivo',
    'voice.error.fileInvalidType': 'Tipo de arquivo inválido',
    'voice.error.fileTooBig': 'O arquivo é muito grande',
    'voice.error.fileTooSmall': 'O arquivo é muito pequeno',
    'voice.error.genericRetryLater': 'Algo deu errado. Tente novamente mais tarde.',
    'voice.error.invalidLink': 'Insira um link válido',
    'voice.error.maxDuration': 'A duração máxima é de 10 minutos',
    'voice.error.maxSamples': 'Você pode enviar até 10 amostras',
    'voice.error.prefix': 'Erro: {message}',
    'voice.error.tooManyFiles': 'Arquivos demais',
    'voice.label.name': 'Nome',
    'voice.language.englishUS': 'Inglês (EUA)',
    'voice.rename.namePlaceholder': 'Digite um novo nome',
    'voice.rename.title': 'Renomear clone de voz personalizado',
    'voice.samples.count': 'Amostras {quantity}/{maxQuantity}',
    'voice.tips.first': 'Áudio ou vídeo de 1 a 10 minutos, com até 10 MB por arquivo',
    'voice.tips.fourth':
      'Para obter uma voz uniforme, evite amostras gravadas em ambientes diferentes ou com estilos de fala distintos',
    'voice.tips.proLabel': 'Dica:',
    'voice.tips.proText':
      'Depois de criar a voz, você poderá ajustar a semelhança e a expressividade. Crie vozes separadas para diferentes estilos de fala, tons e emoções.',
    'voice.tips.second': 'Apenas um locutor, com estilo de fala uniforme e poucas pausas longas',
    'voice.tips.third': 'Som de alta qualidade sem ruído de fundo',
    'voice.upload.linkPlaceholder': 'Cole um link do YouTube ou Vimeo',
    'voice.upload.or': 'ou',
  },
  ru: {
    'voice.action.delete': 'Удалить',
    'voice.aria.removeSample': 'Удалить образец голоса',
    'voice.delete.subtitle':
      'После удаления собственный клон голоса станет недоступен. Если он используется в проектах, его заменит мгновенный клон голоса.',
    'voice.delete.title': 'Удалить этот собственный клон голоса навсегда?',
    'voice.dropzone.choose': 'Нажмите, чтобы выбрать файл',
    'voice.dropzone.drag': 'или перетащите его сюда',
    'voice.dropzone.upload': 'Нажмите, чтобы загрузить файл',
    'voice.error.fileInvalidType': 'Неподдерживаемый тип файла',
    'voice.error.fileTooBig': 'Файл слишком большой',
    'voice.error.fileTooSmall': 'Файл слишком маленький',
    'voice.error.genericRetryLater': 'Что-то пошло не так. Попробуйте позже.',
    'voice.error.invalidLink': 'Введите корректную ссылку',
    'voice.error.maxDuration': 'Максимальная длительность — 10 минут',
    'voice.error.maxSamples': 'Можно загрузить не более 10 образцов',
    'voice.error.prefix': 'Ошибка: {message}',
    'voice.error.tooManyFiles': 'Слишком много файлов',
    'voice.label.name': 'Название',
    'voice.language.englishUS': 'Английский (US)',
    'voice.rename.namePlaceholder': 'Введите новое название',
    'voice.rename.title': 'Переименовать собственный клон голоса',
    'voice.samples.count': 'Образцы: {quantity}/{maxQuantity}',
    'voice.tips.first': 'Каждый образец — аудио или видео длительностью от 1 до 10 минут и размером до 10 МБ',
    'voice.tips.fourth':
      'Для однородной озвучки не загружайте образцы, записанные в разных условиях или с разной манерой речи',
    'voice.tips.proLabel': 'Совет:',
    'voice.tips.proText':
      'После создания голоса можно настроить сходство и выразительность. Создавайте отдельные голоса для разных манер речи, тонов и эмоций.',
    'voice.tips.second': 'Один спикер с одинаковой манерой речи и минимальным количеством долгих пауз',
    'voice.tips.third': 'Качественный звук без фонового шума',
    'voice.upload.linkPlaceholder': 'Вставьте ссылку на YouTube или Vimeo',
    'voice.upload.or': 'или',
  },
});

export const VOICE_TRANSLATIONS = defineDomainTranslations({
  en: { ...CORE_VOICE_TRANSLATIONS.en, ...VOICE_FEATURE_TRANSLATIONS.en },
  es: { ...CORE_VOICE_TRANSLATIONS.es, ...VOICE_FEATURE_TRANSLATIONS.es },
  fr: { ...CORE_VOICE_TRANSLATIONS.fr, ...VOICE_FEATURE_TRANSLATIONS.fr },
  it: { ...CORE_VOICE_TRANSLATIONS.it, ...VOICE_FEATURE_TRANSLATIONS.it },
  pt: { ...CORE_VOICE_TRANSLATIONS.pt, ...VOICE_FEATURE_TRANSLATIONS.pt },
  ru: { ...CORE_VOICE_TRANSLATIONS.ru, ...VOICE_FEATURE_TRANSLATIONS.ru },
});
